// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Condition, Encounter, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';

export interface UsePatientDiagnosesResult {
  diagnoses: Condition[];
  loading: boolean;
  error: Error | null;
  setDiagnoses: (diagnoses: Condition[]) => void;
  refresh: () => void;
}

export function usePatientDiagnoses(patient: Patient | undefined): UsePatientDiagnosesResult {
  const medplum = useMedplum();
  const [diagnoses, setDiagnoses] = useState<Condition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [results, encounters] = await Promise.all([
        medplum.searchResources('Condition', {
          subject: `Patient/${patient.id}`,
          _sort: '-recorded-date',
          _count: '100',
        }),
        medplum.searchResources('Encounter', { subject: `Patient/${patient.id}`, _count: '100' }),
      ]);
      const diagnosisIds = new Set(
        encounters.flatMap((encounter) =>
          (encounter.diagnosis ?? [])
            .map(
              (diagnosis: NonNullable<Encounter['diagnosis']>[number]) => diagnosis.condition?.reference?.split('/')[1]
            )
            .filter(Boolean)
        )
      );
      setDiagnoses(
        (Array.from(results) as Condition[]).filter((condition) => condition.id && diagnosisIds.has(condition.id))
      );
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load diagnoses'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  return { diagnoses, loading, error, setDiagnoses, refresh: fetch };
}
